(function () {
  const CFG = () => window.grefiCheckout || {};
  const isMobile = () => window.matchMedia('(max-width: 749px)').matches || /iPhone|iPad|Android/i.test(navigator.userAgent || '');
  const FASTRR_TIMEOUT_MS = () => (isMobile() ? 16000 : 10000);
  const CHECKOUT_SELECTORS = [
    '[data-grefi-checkout]',
    '#checkout',
    '.cart__checkout-button',
    '.grefi-cart__checkout',
    'button[name="checkout"]',
  ].join(',');

  let checkoutLock = false;
  let pollTimer = null;

  function matchesCheckout(el) {
    if (!el) return false;
    return CHECKOUT_SELECTORS.split(',').some((sel) => {
      try {
        return el.matches(sel) || el.closest(sel);
      } catch {
        return false;
      }
    });
  }

  function shouldGuard() {
    const cfg = CFG();
    if (!cfg.provider || cfg.provider === 'shopify') return false;
    if (cfg.isLoggedIn && !cfg.requireOtpForGuests) return false;
    return true;
  }

  function setLoading(btn, on) {
    if (!btn) return;
    btn.classList.toggle('grefi-checkout--loading', on);
    if (on) btn.setAttribute('aria-busy', 'true');
    else btn.removeAttribute('aria-busy');
  }

  function bodyText() {
    return (document.body && document.body.innerText) || '';
  }

  /** Shiprocket / Fastrr UI is open (including iframe shell) */
  function isCheckoutUiOpen() {
    if (document.getElementById('fastrr-main-container')) return true;
    if (document.getElementById('headless-container')) return true;
    if (document.getElementById('headless-iframe')) return true;
    if (document.querySelector('.headless-modal, .fastrr-main-container iframe, .sr-headless-checkout iframe')) {
      return true;
    }

    const text = bodyText();
    if (/Initiating Checkout/i.test(text) && !/Enter mobile number|10-digit mobile|Verify Your Phone/i.test(text)) {
      return false;
    }

    return !!(
      document.querySelector('iframe[src*="fastrr"]') ||
      document.querySelector('iframe[src*="pickrr"]') ||
      document.querySelector('iframe[src*="shiprocket"]') ||
      document.querySelector('iframe[src*="gokwik"]') ||
      /Enter mobile number|Provide your mobile number|10-digit mobile number|Verify Your Phone Number/i.test(text)
    );
  }

  function isStuckInitiating() {
    if (document.getElementById('headless-container') || document.getElementById('headless-iframe')) {
      return false;
    }
    const text = bodyText();
    return /Initiating Checkout/i.test(text) && !isCheckoutUiOpen();
  }

  function dismissStuckOverlay() {
    if (document.getElementById('headless-container')) return;

    document.querySelectorAll('body *').forEach((el) => {
      try {
        const t = (el.textContent || '').trim();
        if (!t || t.length > 80) return;
        if (!/Initiating Checkout/i.test(t)) return;
        let node = el;
        for (let i = 0; i < 6 && node && node !== document.body; i++) {
          const style = window.getComputedStyle(node);
          const fixed =
            style.position === 'fixed' || style.position === 'absolute' || Number(style.zIndex) > 100;
          if (fixed || /overlay|modal|loader|backdrop|dialog/i.test(node.className || '')) {
            node.style.setProperty('display', 'none', 'important');
            node.style.setProperty('visibility', 'hidden', 'important');
            break;
          }
          node = node.parentElement;
        }
      } catch {
        /* ignore */
      }
    });
  }

  function tryGokwikOrVisibleCta() {
    const candidates = Array.from(
      document.querySelectorAll(
        'button, a, div[role="button"], [id*="gokwik"], [class*="gokwik"], [id*="gkw"], [class*="gkw"]'
      )
    );

    for (const el of candidates) {
      if (el.closest('[data-grefi-checkout], .grefi-cart__checkout, .cart__checkout-button')) continue;
      if (el.getAttribute('aria-hidden') === 'true') continue;
      const label = ((el.getAttribute('aria-label') || '') + ' ' + (el.textContent || '')).trim();
      if (/BUY NOW WITH UPI|UPI\s*\/\s*COD|Powered by\s*GoKwik|gokwik/i.test(label)) {
        const clickable =
          el.closest('button, a, [role="button"]') ||
          el.querySelector('button, a, [role="button"]') ||
          el;
        clickable.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
        if (typeof clickable.click === 'function') clickable.click();
        return true;
      }
    }

    const gokwikRoot = document.querySelector(
      '#gokwik-checkout-button, #gokwik-buyNow, [id*="gokwik-buy"], [class*="gokwik-checkout"], [class*="gokwikBuy"]'
    );
    if (gokwikRoot) {
      const btn = gokwikRoot.matches('button, a')
        ? gokwikRoot
        : gokwikRoot.querySelector('button, a, [role="button"]') || gokwikRoot;
      btn.click();
      return true;
    }

    return false;
  }

  function setDiscountCookie(code) {
    if (!code) return;
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `discount_code=${encodeURIComponent(code)}; path=/; SameSite=Lax${secure}`;
    try {
      sessionStorage.setItem('gbbDiscount', code);
      sessionStorage.setItem('discount_code', code);
      sessionStorage.setItem('fastrr_discount_code', code);
    } catch {
      /* private mode */
    }
  }

  /** Pull applied Shopify codes so Shiprocket/Fastrr modal can reuse them */
  async function syncCartDiscounts() {
    const cfg = CFG();
    let codes = Array.isArray(cfg.discountCodes) ? cfg.discountCodes.filter(Boolean) : [];

    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (res.ok) {
        const cart = await res.json();
        const fromCart = (cart.discount_codes || [])
          .filter((d) => d && d.applicable !== false && d.code)
          .map((d) => String(d.code).trim())
          .filter(Boolean);
        if (fromCart.length) codes = fromCart;
      }
    } catch {
      /* keep liquid codes */
    }

    codes = [...new Set(codes.map((c) => String(c).trim()).filter(Boolean))];
    cfg.discountCodes = codes;

    if (codes.length) {
      const primary = codes[0];
      setDiscountCookie(primary);
      /* Shopify fallback checkout also needs ?discount= */
      const base = (cfg.checkoutUrl || '/checkout').split('?')[0];
      cfg.checkoutUrl = `${base}?discount=${encodeURIComponent(codes.join(','))}`;
    }

    return codes;
  }

  function tryFastrrApis() {
    /* Fastrr throws CHECKOUT_ALREADY_INIT if started twice */
    if (document.getElementById('fastrr-main-container') || document.getElementById('headless-container')) {
      return true;
    }

    const evt = new MouseEvent('click', { bubbles: true, cancelable: true, view: window });
    const codes = (CFG().discountCodes || []).filter(Boolean);
    if (codes[0]) setDiscountCookie(codes[0]);

    /* HeadlessCheckout.addToCart needs a cart token; calling it bare hangs on "Initiating Checkout" */
    if (window.HeadlessCheckout && typeof window.HeadlessCheckout.checkout === 'function') {
      try {
        window.HeadlessCheckout.checkout(evt, codes[0] ? { discount_code: codes[0], coupon_code: codes[0] } : undefined);
      } catch {
        window.HeadlessCheckout.checkout(evt);
      }
      return true;
    }
    if (typeof window.shiprocketCheckoutBuyCartHandler === 'function') {
      window.shiprocketCheckoutBuyCartHandler();
      return true;
    }
    if (window.shiprocketCheckoutEvents && typeof window.shiprocketCheckoutEvents.buyCart === 'function') {
      window.shiprocketCheckoutEvents.buyCart(evt);
      return true;
    }

    const mountBtn = document.querySelector('.shiprocket-headless[data-type="cart"] .sr-headless-checkout');
    if (mountBtn) {
      mountBtn.click();
      return true;
    }

    return false;
  }

  function waitForFastrr(maxMs) {
    return new Promise((resolve) => {
      if (tryFastrrApis() || tryGokwikOrVisibleCta()) {
        resolve(true);
        return;
      }
      const start = Date.now();
      const timer = setInterval(() => {
        if (tryFastrrApis() || tryGokwikOrVisibleCta()) {
          clearInterval(timer);
          resolve(true);
          return;
        }
        if (Date.now() - start >= maxMs) {
          clearInterval(timer);
          resolve(false);
        }
      }, 250);
    });
  }

  function tryReleasitCheckout() {
    const rsiBtn = document.querySelector(
      '[class*="rsi-cod-form"], .rsi-cod-form-button, a[href*="rsi-btn-hook"], [data-rsi-cod]'
    );
    if (rsiBtn) {
      rsiBtn.click();
      return true;
    }
    return false;
  }

  function goShopifyCheckout() {
    /* Never yank user away if Shiprocket modal is already up */
    if (isCheckoutUiOpen()) return;
    dismissStuckOverlay();
    const cfg = CFG();
    window.location.href = cfg.checkoutUrl || '/checkout';
  }

  function clearPoll() {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  }

  function watchUntilOpenOrFallback(trigger) {
    const startedAt = Date.now();
    const limit = FASTRR_TIMEOUT_MS();
    clearPoll();

    pollTimer = setInterval(() => {
      if (isCheckoutUiOpen()) {
        clearPoll();
        setLoading(trigger, false);
        checkoutLock = false;
        return;
      }

      const stuck = isStuckInitiating() && Date.now() - startedAt > 5000;
      const timedOut = Date.now() - startedAt >= limit;

      if (stuck || timedOut) {
        clearPoll();
        /* If Shiprocket shell appeared mid-poll, keep it */
        if (isCheckoutUiOpen()) {
          setLoading(trigger, false);
          checkoutLock = false;
          return;
        }

        dismissStuckOverlay();
        setLoading(trigger, false);
        checkoutLock = false;

        if (tryGokwikOrVisibleCta() || tryFastrrApis()) {
          setTimeout(() => {
            if (!isCheckoutUiOpen()) goShopifyCheckout();
          }, isMobile() ? 4000 : 2500);
          return;
        }

        goShopifyCheckout();
      }
    }, 350);
  }

  async function initiateCheckout(trigger) {
    if (checkoutLock) return;
    checkoutLock = true;

    const cfg = CFG();
    const provider = cfg.provider || 'fastrr';

    setLoading(trigger, true);

    /* Cart pe laga coupon Shiprocket modal tak le jao */
    await syncCartDiscounts();

    if (provider === 'releasit') {
      if (tryReleasitCheckout()) {
        watchUntilOpenOrFallback(trigger);
        return;
      }
      checkoutLock = false;
      setLoading(trigger, false);
      goShopifyCheckout();
      return;
    }

    if (provider === 'fastrr' || provider === 'auto') {
      /* Mobile: wait for Fastrr script (often slower on 4G) */
      const waitMs = isMobile() ? 4000 : 1500;
      const started = await waitForFastrr(waitMs);

      if (started || tryGokwikOrVisibleCta() || tryFastrrApis()) {
        watchUntilOpenOrFallback(trigger);
        return;
      }

      if (provider === 'auto' && tryReleasitCheckout()) {
        watchUntilOpenOrFallback(trigger);
        return;
      }
    }

    checkoutLock = false;
    setLoading(trigger, false);
    goShopifyCheckout();
  }

  function handleClick(e) {
    if (!shouldGuard()) return;

    const target = e.target.closest('button, a, input[type="submit"]');
    if (!matchesCheckout(target)) return;

    if (
      target.closest('[id*="gokwik"], [class*="gokwik"], [id*="gkw"], [class*="gkw"]') &&
      !target.matches(CHECKOUT_SELECTORS) &&
      !target.closest('[data-grefi-checkout], .grefi-cart__checkout, .cart__checkout-button')
    ) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    initiateCheckout(target);
  }

  function handleSubmit(e) {
    if (!shouldGuard()) return;

    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.action.includes('/cart')) return;

    const submitter = e.submitter;
    const isCheckout =
      (submitter && submitter.name === 'checkout') ||
      (submitter && matchesCheckout(submitter)) ||
      form.querySelector('[name="checkout"]:focus');

    if (!isCheckout) return;

    e.preventDefault();
    e.stopPropagation();
    initiateCheckout(submitter || form.querySelector('[name="checkout"]'));
  }

  document.addEventListener('click', handleClick, true);
  document.addEventListener('submit', handleSubmit, true);
  /* iOS Safari sometimes needs touchend for custom buttons */
  document.addEventListener(
    'touchend',
    (e) => {
      if (!shouldGuard()) return;
      const target = e.target.closest('[data-grefi-checkout], .grefi-cart__checkout, .cart__checkout-button');
      if (!target) return;
      e.preventDefault();
      e.stopPropagation();
      initiateCheckout(target);
    },
    { capture: true, passive: false }
  );

  window.grefiStartCheckout = function (trigger) {
    initiateCheckout(trigger || null);
  };

  /* Expose so PDP never force-redirects while Shiprocket is open */
  window.grefiCheckoutIsOpen = isCheckoutUiOpen;

  function relocateAccelButtons() {
    const slots = document.querySelectorAll('[data-grefi-accel-slot]');
    if (!slots.length) return;
    const slot = slots[slots.length - 1];

    document
      .querySelectorAll(
        '.cart-drawer__summary > [id*="gokwik"], .cart-drawer__summary > [class*="gokwik"], .grefi-cart__summary-card > [id*="gokwik"], .grefi-cart__summary-card > [class*="gokwik"], #gokwik-checkout-button, #gokwik-buyNow'
      )
      .forEach((node) => {
        if (!slot.contains(node)) slot.appendChild(node);
      });
  }

  function watchAccelButtons() {
    relocateAccelButtons();
    const root = document.querySelector('cart-drawer-component, grefi-cart, body');
    if (!root) return;
    const mo = new MutationObserver(() => relocateAccelButtons());
    mo.observe(root, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchAccelButtons);
  } else {
    watchAccelButtons();
  }
})();
